import React, { useState, useEffect } from 'react';
import { Typography, FlexGrid, Notification, Button, Spinner } from '@telus-uds/components-web';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { reportsAPI, exportReportExcel, exportReportCsv } from '../utils/apiService';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  formatNumber,
  getDefaultDateRange
} from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import ChartDescription from '../components/ChartDescription';
import LastUpdated from '../components/LastUpdated';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { chartCardStyle } from '../utils/chartStyles';
import 'react-datepicker/dist/react-datepicker.css';

const FilterSection = styled.div`
  background: #f7f7f7;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PeriodGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const PeriodTitle = styled.h3`
  font-size: 1.1rem;
  color: #4b286d;
  margin-bottom: 1rem;
`;

const DateInputGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

const DateLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #454545;

  input {
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: #4b286d;
    }
  }
`;

const ComparisonSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  align-items: center;
  margin: 2rem 0;
`;

const PeriodStats = styled.div`
  background: #ffffff;
  border: 2px solid #e4e4e4;
  border-radius: 8px;
  padding: 1.5rem;
`;

const DifferenceIndicator = styled.div`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color || '#666666'};
`;

const DateRangeComparison = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const [trendWeeks, setTrendWeeks] = useState(8);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);

  const defaultRange = getDefaultDateRange();
  const [period1Start, setPeriod1Start] = useState(new Date(defaultRange.startDate));
  const [period1End, setPeriod1End] = useState(new Date(defaultRange.endDate));
  const [period2Start, setPeriod2Start] = useState(new Date(defaultRange.startDate));
  const [period2End, setPeriod2End] = useState(new Date(defaultRange.endDate));
  // Snapshot of whichever periods produced the data currently on screen — export must use
  // this, not the live date pickers, or changing a date without clicking Compare would export
  // a file that doesn't match what's displayed.
  const [appliedParams, setAppliedParams] = useState(null);

  // refresh=true asks the server to skip its cached result (Refresh button).
  const fetchData = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      // Format dates in local timezone to avoid UTC conversion issues
      const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const params = {
        startDate1: formatLocalDate(period1Start),
        endDate1: formatLocalDate(period1End),
        startDate2: formatLocalDate(period2Start),
        endDate2: formatLocalDate(period2End)
      };
      setAppliedParams(params);
      const result = await reportsAPI.getDateRangeComparison(refresh === true ? { ...params, refresh: true } : params);
      setData(result.data);
      setMeta({ generatedAt: result.generated_at, throttled: Boolean(result.refresh_throttled) });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch comparison data'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = () => {
    fetchData();
  };

  const fetchTrendData = async weeks => {
    setTrendLoading(true);
    setTrendError(null);
    try {
      const result = await reportsAPI.getMultiPeriodTrend({ weeks });
      setTrendData(result.data);
    } catch (err) {
      setTrendError(getApiErrorMessage(err, 'Failed to fetch multi-period trend'));
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => {
    fetchTrendData(trendWeeks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendWeeks]);

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const params = appliedParams || {
        startDate1: getDefaultDateRange().startDate,
        endDate1: getDefaultDateRange().endDate,
        startDate2: getDefaultDateRange().startDate,
        endDate2: getDefaultDateRange().endDate
      };
      await exportReportCsv('/reports/date-range-comparison/export/csv', params, 'date-range-comparison');
    } catch (err) {
      alert('Failed to export CSV file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const params = appliedParams || {
        startDate1: getDefaultDateRange().startDate,
        endDate1: getDefaultDateRange().endDate,
        startDate2: getDefaultDateRange().startDate,
        endDate2: getDefaultDateRange().endDate
      };
      const fileName = `date-range-comparison-${params.startDate1}-vs-${params.startDate2}.xlsx`;
      await exportReportExcel('/reports/date-range-comparison/export/excel', params, fileName);
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Loading/error states render inline below (inside the period-selector return), not as
  // early returns here — this fetch only happens after the user clicks "Compare Periods", so
  // replacing the whole page with a bare spinner/error would hide the date inputs they'd need
  // to fix and retry.
  const renderContent = () => {
    if (loading) {
      return <LoadingSpinner text="Comparing date ranges..." />;
    }

    if (!data) {
      return (
        <EmptyState
          title="Ready to Compare"
          description="Select date ranges and click Compare to see the results."
        />
      );
    }

    // Backend returns range1, range2, comparison (not period1, period2)
    const { range1, range2, comparison } = data;
    
    // Extract stats from ranges
    const period1Stats = range1 && range1.stats;
    const period2Stats = range2 && range2.stats;

    const getDifferenceColor = diff => {
      if (diff > 0) return '#00A600';
      if (diff < 0) return '#CC0000';
      return '#666666';
    };

    // Calculate actual differences from the stats
    const ordersP1 = parseInt((period1Stats && period1Stats.orders && period1Stats.orders.total_orders) || 0, 10);
    const ordersP2 = parseInt((period2Stats && period2Stats.orders && period2Stats.orders.total_orders) || 0, 10);
    const ordersDiff = ordersP2 - ordersP1;

    const submittedP1 = parseInt((period1Stats && period1Stats.orders && period1Stats.orders.submitted) || 0, 10);
    const submittedP2 = parseInt((period2Stats && period2Stats.orders && period2Stats.orders.submitted) || 0, 10);
    const submittedDiff = submittedP2 - submittedP1;

    const pdfsP1 = parseInt((period1Stats && period1Stats.pdfs && period1Stats.pdfs.summary && period1Stats.pdfs.summary.total_pdfs_generated) || 0, 10);
    const pdfsP2 = parseInt((period2Stats && period2Stats.pdfs && period2Stats.pdfs.summary && period2Stats.pdfs.summary.total_pdfs_generated) || 0, 10);
    const pdfsDiff = pdfsP2 - pdfsP1;

    const emailsP1 = parseInt((period1Stats && period1Stats.emails && period1Stats.emails.summary && period1Stats.emails.summary.total_emails_sent) || 0, 10);
    const emailsP2 = parseInt((period2Stats && period2Stats.emails && period2Stats.emails.summary && period2Stats.emails.summary.total_emails_sent) || 0, 10);
    const emailsDiff = emailsP2 - emailsP1;

    // Prepare comparison chart data
    const comparisonData = [
      {
        metric: 'Orders',
        period1: ordersP1,
        period2: ordersP2
      },
      {
        metric: 'PDFs',
        period1: pdfsP1,
        period2: pdfsP2
      },
      {
        metric: 'Emails',
        period1: emailsP1,
        period2: emailsP2
      }
    ];

    return (
      <>
        <LastUpdated
        generatedAt={meta && meta.generatedAt}
        throttled={Boolean(meta && meta.throttled)}
        loading={loading}
        onRefresh={() => fetchData(true)}
      />

      {/* Summary Comparison */}
        <div style={{ marginBottom: '3rem' }}>
          <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1rem' }}>
            Overall Comparison
          </Typography>
          <p style={{ color: '#666666', marginBottom: '1rem', fontSize: '0.875rem' }}>
            Change shown below is <strong>Period 2 minus Period 1</strong> — a positive value
            means Period 2 is higher.
          </p>
          <FlexGrid>
            <FlexGrid.Row>
              <FlexGrid.Col xs={12} md={4}>
              <SummaryCard
                title="Orders Created: Change (Period 2 vs Period 1)" descriptionKey="comparisonOrders"
                value={formatNumber(Math.abs(ordersDiff))}
                color={getDifferenceColor(ordersDiff)}
                icon={ordersDiff > 0 ? '📈' : '📉'}
                subText={`${ordersDiff > 0 ? '+' : ''}${formatNumber(ordersDiff)}`}
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4}>
              <SummaryCard
                title="Orders Submitted: Change (Period 2 vs Period 1)" descriptionKey="ordersSubmitted"
                value={formatNumber(Math.abs(submittedDiff))}
                color={getDifferenceColor(submittedDiff)}
                icon={submittedDiff > 0 ? '📈' : '📉'}
                subText={`${submittedDiff > 0 ? '+' : ''}${formatNumber(submittedDiff)}`}
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4}>
              <SummaryCard
                title="PDFs: Change (Period 2 vs Period 1)" descriptionKey="comparisonPdfs"
                value={formatNumber(Math.abs(pdfsDiff))}
                color={getDifferenceColor(pdfsDiff)}
                icon={pdfsDiff > 0 ? '📈' : '📉'}
                subText={`${pdfsDiff > 0 ? '+' : ''}${formatNumber(pdfsDiff)}`}
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4}>
              <SummaryCard
                title="Emails: Change (Period 2 vs Period 1)" descriptionKey="comparisonEmails"
                value={formatNumber(Math.abs(emailsDiff))}
                color={getDifferenceColor(emailsDiff)}
                icon={emailsDiff > 0 ? '📈' : '📉'}
                subText={`${emailsDiff > 0 ? '+' : ''}${formatNumber(emailsDiff)}`}
              />
              </FlexGrid.Col>
            </FlexGrid.Row>
          </FlexGrid>
        </div>

        {/* Side-by-Side Comparison */}
        <ComparisonSection>
          <PeriodStats>
            <Typography heading="h3" variant={{ size: 'h3' }} block style={{ color: '#0066CC', marginBottom: '1rem' }}>
              Period 1
              {range1 && range1.period && (
                <div style={{ fontSize: '0.75rem', fontWeight: 'normal', marginTop: '0.25rem' }}>
                  {range1.period.start} to {range1.period.end}
                </div>
              )}
            </Typography>
              <ChartDescription descriptionKey="periodOne" />
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Orders:</strong> {formatNumber(ordersP1)}
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>PDFs:</strong> {formatNumber(pdfsP1)}
            </div>
            <div>
              <strong>Emails:</strong> {formatNumber(emailsP1)}
            </div>
          </PeriodStats>

          <DifferenceIndicator color={getDifferenceColor(ordersDiff)}>
            {ordersDiff > 0 ? '→' : '←'}
          </DifferenceIndicator>

          <PeriodStats>
            <Typography heading="h3" variant={{ size: 'h3' }} block style={{ color: '#FF9900', marginBottom: '1rem' }}>
              Period 2
              {range2 && range2.period && (
                <div style={{ fontSize: '0.75rem', fontWeight: 'normal', marginTop: '0.25rem' }}>
                  {range2.period.start} to {range2.period.end}
                </div>
              )}
            </Typography>
              <ChartDescription descriptionKey="periodTwo" />
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Orders:</strong> {formatNumber(ordersP2)}
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>PDFs:</strong> {formatNumber(pdfsP2)}
            </div>
            <div>
              <strong>Emails:</strong> {formatNumber(emailsP2)}
            </div>
          </PeriodStats>
        </ComparisonSection>

        {/* Charts */}
        <FlexGrid style={{ marginTop: '2rem' }}>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  Period Comparison
                </Typography>
              <ChartDescription descriptionKey="periodComparison" />
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={comparisonData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metric" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="period1" fill="#0066CC" name="Period 1" />
                    <Bar dataKey="period2" fill="#FF9900" name="Period 2" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </>
    );
  };

  return (
    <div>
      {error && (
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      )}
      <FilterSection>
        <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1rem' }}>Select Periods to Compare</Typography>

        <PeriodGroup>
          <PeriodTitle>Period 1</PeriodTitle>
          <DateInputGroup>
            <DateLabel>
              Start Date
              <DatePicker
                selected={period1Start}
                onChange={date => setPeriod1Start(date)}
                dateFormat="yyyy-MM-dd"
              />
            </DateLabel>
            <DateLabel>
              End Date
              <DatePicker
                selected={period1End}
                onChange={date => setPeriod1End(date)}
                dateFormat="yyyy-MM-dd"
              />
            </DateLabel>
          </DateInputGroup>
        </PeriodGroup>

        <PeriodGroup>
          <PeriodTitle>Period 2</PeriodTitle>
          <DateInputGroup>
            <DateLabel>
              Start Date
              <DatePicker
                selected={period2Start}
                onChange={date => setPeriod2Start(date)}
                dateFormat="yyyy-MM-dd"
              />
            </DateLabel>
            <DateLabel>
              End Date
              <DatePicker
                selected={period2End}
                onChange={date => setPeriod2End(date)}
                dateFormat="yyyy-MM-dd"
              />
            </DateLabel>
          </DateInputGroup>
        </PeriodGroup>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <Button variant={{ priority: 'high' }} onClick={handleCompare}>
            Compare Periods
          </Button>
          {data && (
            <Spinner label="Exporting..." variant={{ size: 'small' }} show={isExporting} inline>
              <Button
                variant={{ priority: 'low' }}
                onClick={handleExportExcel}
                disabled={isExporting}
                style={{ marginLeft: '1rem' }}
              >
                📊 Export to Excel
              </Button>
              <Button
                variant={{ priority: 'low' }}
                onClick={handleExportCsv}
                disabled={isExporting}
                style={{ marginLeft: '1rem' }}
              >
                📄 Export to CSV
              </Button>
            </Spinner>
          )}
        </div>
      </FilterSection>

      {renderContent()}

      <div style={{ marginTop: '3rem' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem'
        }}
        >
          <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d' }}>
            Multi-Period Trend
          </Typography>
          <DateLabel>
            Weeks
            <select
              value={trendWeeks}
              onChange={e => setTrendWeeks(Number(e.target.value))}
              style={{
                padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.875rem'
              }}
            >
              <option value={4}>Last 4 weeks</option>
              <option value={8}>Last 8 weeks</option>
              <option value={12}>Last 12 weeks</option>
              <option value={26}>Last 26 weeks</option>
              <option value={52}>Last 52 weeks</option>
            </select>
          </DateLabel>
        </div>

        {trendError && (
          <Notification variant={{ validation: 'error' }}>
            <div>
              <strong>Error:</strong> {trendError}
            </div>
          </Notification>
        )}

        {trendLoading && <LoadingSpinner text="Loading multi-period trend..." />}

        {!trendLoading && trendData && trendData.trend && trendData.trend.length > 0 && (
          <div style={chartCardStyle}>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart
                data={trendData.trend.map(item => ({
                  week: new Date(item.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                  orders: item.orders,
                  pdfs: item.pdfs,
                  emails: item.emails
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="orders" stroke="#4b286d" strokeWidth={2} name="Orders" />
                <Line type="monotone" dataKey="pdfs" stroke="#0066CC" strokeWidth={2} name="PDFs" />
                <Line type="monotone" dataKey="emails" stroke="#00A600" strokeWidth={2} name="Emails" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeComparison;
